/***
 *
 *   HEADER
 *   The app top bar. Ported from
 *   ui-reference/src/components/layouts/top-bar.tsx.
 *
 *   Slots, left to right: sidebar collapse toggle · breadcrumbs · tabs ·
 *   actions · project switcher · user menu.
 *
 *   ADAPTATION. The reference reads `title` / `tabs` / `actions` from a Zustand
 *   store (`useTopBarStore`) that each route writes into. This app has no such
 *   store and does not need one: the route title is already resolved by
 *   BreadcrumbsContext, so the reference's single-line `title` slot is filled by
 *   the (richer) breadcrumb trail instead. `tabs` and `actions` stay props, as
 *   the live `<Header>` call site already passed children.
 *
 *   PROPS
 *   title: current view title — kept for the document title / callers, the
 *          trail itself comes from BreadcrumbsContext
 *   tabs: node rendered in the centre slot, md and up (optional)
 *   children: actions rendered on the trailing edge (optional)
 *
 **********/

import { Breadcrumbs, Icon, Tooltip, ProjectSelector, cn } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useSidebarContext } from 'context/SidebarContext';
import { UserMenu } from './userMenu';
import Style from './header.tailwind.js';

export function Header(props) {
    const { _t } = useTranslate();
    const { isCollapsed, toggle } = useSidebarContext();

    const toggleLabel = isCollapsed ? _t('sidebar.expand') : _t('sidebar.collapse');

    return (
        <header className={cn(Style.header, props.className)}>
            <div className={Style.leading}>
                <Tooltip label={toggleLabel} side="bottom" delayDuration={250} className={Style.toggleTooltip}>
                    <button
                        type="button"
                        onClick={toggle}
                        aria-expanded={!isCollapsed}
                        aria-controls="app-sidebar-nav"
                        aria-label={toggleLabel}
                        className={Style.toggle}>
                        {/* feather has no paired panel glyph — chevrons carry the
                            same open/close meaning. See PLAN.md appendix B. */}
                        <Icon
                            image={isCollapsed ? 'chevrons-right' : 'chevrons-left'}
                            size={16}
                            color="currentColor"
                        />
                    </button>
                </Tooltip>

                <Breadcrumbs />
            </div>

            {props.tabs && <div className={Style.tabs}>{props.tabs}</div>}

            <div className={Style.trailing}>
                {props.children}

                <div className={cn(Style.identity, props.children && Style.identityDivided)}>
                    <ProjectSelector />
                    <UserMenu />
                </div>
            </div>
        </header>
    );
}
